import { memo, type MouseEvent, useCallback, useRef } from 'react'

import { useGalleryDisplay } from '@/components/layout/Gallery'
import { type RecType } from '@/defs/rec'
import { type StatType } from '@/defs/stat'
import { useCardModalButton } from '@/hooks/useCardModalButton'
import { useCardsRec } from '@/hooks/useCardsRec'
import { useInView } from '@/hooks/useInView'
import { cn } from '@/utils/commonUtil'

import { CardControls } from '../CardControls/CardControls'
import { CardFlags } from '../CardFlags/CardFlags'
import { CardStats } from '../CardStats/CardStats'

import styles from './CardRec.module.scss'
import { CardRecFaces } from './CardRecFaces/CardRecFaces'

type Props = {
  assumeInView?: boolean
  clickOpensModal?: boolean
  controls?: 'left' | 'right' | 'none'
  disableLink?: boolean
  disableModal?: boolean
  extend?: boolean
  flagsHide?: boolean
  forceLoad?: boolean
  href?: string
  openLinksInNewTab?: boolean
  rec: RecType
  showPrices?: boolean
  size?: 'sm'
  stats?: StatType[]
  statsHide?: boolean
}

const CardRecInner = ({
  assumeInView,
  clickOpensModal,
  controls = 'right',
  disableLink,
  disableModal,
  extend,
  flagsHide,
  forceLoad,
  href: hrefProp,
  openLinksInNewTab,
  rec,
  showPrices,
  size,
  stats: statsProp,
  statsHide,
}: Props) => {
  const { stats: statsGallery, statsRoles } = useGalleryDisplay()
  const stats = statsProp || (rec.role && statsRoles?.[rec.role]) || statsGallery
  const { inViewStatus, ref } = useInView({ assumeInView, forceLoad })
  const { onlyBack, onlyFront, onlyPartner } = rec
  const { card } = useCardsRec({ enabled: inViewStatus !== 'outside', rec })
  const hasContentWarning = !!card?.hasContentWarning
  const sideRef = useRef<HTMLDivElement>(null)

  const { cardLink, onClick: openModal } = useCardModalButton({ rec })
  const href = disableLink ? undefined : hrefProp ? hrefProp : cardLink
  const linkProps = href
    ? {
        href,
        newTab: openLinksInNewTab,
      }
    : undefined

  const isPartner = !!rec.partnerRef
  const isDfc = !!card?.nameBack && !!card.imageBack

  const isInView = inViewStatus === 'inView'
  const showFlags = isInView && !hasContentWarning && !flagsHide && controls !== 'none'
  const hasStats = !statsHide && !!stats?.length && controls !== 'none'
  const showStats = !hasContentWarning && hasStats
  const showControls = isInView && !hasContentWarning && controls !== 'none'

  const onFacesClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (disableModal || e.defaultPrevented) return
      // Checks if tiny mode is on (side is display: none and opens modal)
      const isTiny = !!sideRef.current && getComputedStyle(sideRef.current).display === 'none'
      if (clickOpensModal || (controls !== 'none' && isTiny)) openModal(e)
    },
    [clickOpensModal, controls, disableModal, openModal],
  )

  return (
    <div
      className={cn(styles.container, {
        controls,
        extend,
        hasStats,
        isDfc,
        isPartner,
        onlyBack,
        onlyFront,
        onlyPartner,
        size,
        styles,
      })}
      ref={ref}
    >
      <div className={styles.inner}>
        <div className={styles.faces}>
          <CardRecFaces
            animate={!forceLoad}
            inViewStatus={inViewStatus}
            linkProps={linkProps}
            onClick={onFacesClick}
            rec={rec}
            showPrices={showPrices}
          />
        </div>
        <div className={styles.side} ref={sideRef}>
          {showFlags && <CardFlags controls={controls} rec={rec} size={size} />}
          {showControls && <CardControls controls={controls} disableModal={disableModal} rec={rec} size={size} />}
        </div>
        {showStats && <CardStats isInView={inViewStatus !== 'outside'} rec={rec} size={size} stats={stats} />}
      </div>
    </div>
  )
}

export const CardRec = memo(CardRecInner)
